import { FormProvider } from "react-hook-form";

import { useStyles } from "./styles/CompanyForm.styles";
import { Button, Popin, Text } from "$components";
import { Company } from "$types/Company";
import useAuth from "$hooks/useAuth";
import useJStyles from "$hooks/useJStyles";
import { CompanyType } from "$types/CompanyType";
import useCompanyFormDetails from "./hooks/useCompanyFormDetails";
import useCompany from "$hooks/useCompany";

import Activities from "./sections/Activities";
import CompanyTypes from "./sections/CompanyTypes";
import NotesDetails from "./sections/NotesDetails";
import RelationshipsDetails from "./sections/RelationshipsDetails";
import AddressDetails from "./sections/AddressDetails";
import CompanyDetails from "./sections/CompanyDetails";

export type CompanyForm = Company & { contributingCompanies: Company[] };

type Props = {
  companyError?: any;
  defaultValues?: Partial<Company>;
  isSubmitting?: boolean;
  onSubmit: (data: any) => void;
  onReset: () => void;
  readOnlyFields?: Array<keyof Company>;
};

function EntrepriseForm({
  companyError,
  defaultValues,
  isSubmitting = false,
  onReset,
  onSubmit,
  readOnlyFields = [],
}: Props) {
  const classes = useStyles();
  const { jclasses } = useJStyles();
  const { isAdmin } = useAuth();
  const { isAgency } = useCompany();

  // @todo amir: can put all of this in a context ?
  const {
    isOpened,
    companies,
    formDefaultValues,
    relatedCarrierId,
    country,
    companyTypes,
    methods,
    isAutofillAlreadyUsed,
    isAutofillError,
    isCompanyFilledUsed,
    matchingCompanies,
    siretValue,

    setIsCompanyFilledUsed,
    handleSubmit,
    validateName,
    validateSiret,
    fuzzySearchOnCompaniesName,
    handleAutoFillClicked,
    togglePopin,
    handleSubmitCarrier,
  } = useCompanyFormDetails({ defaultValues });

  return (
    <div
      className={jclasses.jWrapper}
      style={{
        width: "700px",
      }}
    >
      <FormProvider {...methods}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSubmit(onSubmit)(e);
          }}
          className={jclasses.form}
        >
          <div className={jclasses.formTop}>
            <Text type="h2">
              {formDefaultValues?._id
                ? "Éditer l'entreprise"
                : isAgency(defaultValues?.companyTypes)
                ? "Ajouter une agence transporteur"
                : "Ajouter une entreprise"}
            </Text>
          </div>

          <CompanyTypes
            companyId={defaultValues?._id}
            initialCompanyTypes={defaultValues?.companyTypes || []}
            isReadOnly={readOnlyFields.includes("companyTypes")}
          />

          <CompanyDetails
            companyTypes={companyTypes}
            defaultValues={defaultValues}
            formDefaultValues={formDefaultValues}
            validateName={validateName}
            validateSiret={validateSiret}
            fuzzySearchOnCompaniesName={fuzzySearchOnCompaniesName}
            isAutofillAlreadyUsed={isAutofillAlreadyUsed}
            isAutofillError={isAutofillError}
            isCompanyFilledUsed={isCompanyFilledUsed}
            setIsCompanyFilledUsed={setIsCompanyFilledUsed}
            matchingCompanies={matchingCompanies}
            siretValue={siretValue}
            handleAutoFillClicked={handleAutoFillClicked}
          />

          <AddressDetails country={country} companyTypes={companyTypes} />

          <RelationshipsDetails
            companyTypes={companyTypes}
            readOnlyFields={readOnlyFields}
          />

          <Activities
            companyTypes={companyTypes}
            relatedCarrierId={relatedCarrierId}
            defaultValues={defaultValues}
            companies={companies}
          />

          {isAgency(companyTypes) && <NotesDetails />}

          {/* error message */}
          {companyError ? (
            <Text className={classes.errorContainer}>{companyError}</Text>
          ) : null}

          {/* footer */}
          {isAdmin() && (
            <div className={jclasses.footerForm}>
              <Button color="secondary" onClick={onReset}>
                Annuler
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                Enregistrer
              </Button>
            </div>
          )}
        </form>

        <Popin openPopin={isOpened} toggleDrawer={togglePopin}>
          <EntrepriseForm
            companyError={companyError}
            defaultValues={{ companyTypes: [CompanyType.CARRIER] }}
            onReset={() => togglePopin(false)}
            onSubmit={handleSubmitCarrier}
            readOnlyFields={["companyTypes"]}
          />
        </Popin>
      </FormProvider>
    </div>
  );
}

export default EntrepriseForm;
